class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // Wire click/keydown on every cart icon across all header instances.
    document.querySelectorAll('[id="cart-icon-bubble"]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });

    // Keep every cart bubble in sync.
    // Shopify's own JS always updates only the first element it finds via
    // getElementById('cart-icon-bubble'). A MutationObserver on that primary
    // element propagates innerHTML changes to every duplicate automatically,
    // covering all update paths (add-to-cart, quantity changes, etc.).
    const primary = document.querySelector('[id="cart-icon-bubble"]');
    if (primary) {
      new MutationObserver(() => {
        const html = primary.innerHTML;
        document.querySelectorAll('[id="cart-icon-bubble"]').forEach((el) => {
          if (el !== primary) el.innerHTML = html;
        });
      }).observe(primary, { childList: true, subtree: true, characterData: true });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const emptyDrawerContent = this.querySelector('.drawer__inner-empty');
        const drawerContent = document.getElementById('CartDrawer');
        const containerToTrapFocusOn =
          this.classList.contains('is-empty') && emptyDrawerContent ? emptyDrawerContent : drawerContent;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active', 'animate', 'is-open');
    this.style.removeProperty('visibility');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.syncEmptyState(parsedState);
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncEmptyState(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = parsed.querySelector(selector);
    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  syncEmptyState(parsedState) {
    if (!parsedState) return;

    let isEmpty = null;

    if (typeof parsedState.item_count === 'number') {
      isEmpty = parsedState.item_count === 0;
    } else if (typeof parsedState.sections?.['cart-drawer'] === 'string') {
      const parsed = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
      const parsedDrawer = parsed.querySelector('cart-drawer');
      if (parsedDrawer) {
        isEmpty = parsedDrawer.classList.contains('is-empty');
      } else if (parsed.querySelector('.drawer__inner-empty')) {
        isEmpty = true;
      } else if (parsed.querySelector('.drawer__cart-items-wrapper')) {
        isEmpty = false;
      }
    }

    if (typeof isEmpty === 'boolean') {
      this.classList.toggle('is-empty', isEmpty);
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) drawerInner.classList.toggle('is-empty', isEmpty);
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // The real id, not the file name: this section sits in a section group, and
    // asking for 'cart-drawer' renders the file with no blocks — which flattens
    // the progress bar's milestones. See cartDrawerSectionId() in norse-cart-ui.js.
    const drawerHost = document.querySelector('cart-drawer');
    const drawerWrapper = drawerHost && drawerHost.closest('[id^="shopify-section-"]');
    const drawerSection = drawerWrapper
      ? drawerWrapper.id.replace(/^shopify-section-/, '')
      : 'cart-drawer';

    return [
      {
        id: 'CartDrawer',
        section: drawerSection,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Bundle-aware cart: qty sync, grouped remove, subscription cleanup (_bundle_id).
(function initNbbbCartBundleSync() {
  if (window._nbbbCartExtended) return;
  window._nbbbCartExtended = true;

  const ROW_SELECTOR = '.cart-item, [id^="CartDrawer-Item-"], [id^="CartItem-"]';
  const SUB_CLEANUP_DELAY_MS = 1500;

  const cache = { byKey: {}, byLine: {}, refreshInFlight: false };
  let bulkUpdateInFlight = false;

  function norseCartUi(method) {
    const api = window.NorseCartUI;
    const fn = api && api[method];
    return typeof fn === 'function' ? fn : null;
  }

  function parseLine(line) {
    const n = parseInt(line, 10);
    return Number.isFinite(n) ? n : null;
  }

  function normalizeItemKey(key) {
    try {
      return decodeURIComponent(String(key || '')).trim();
    } catch (_) {
      return String(key || '').trim();
    }
  }

  function bundleIdFromProperties(properties) {
    const p = properties || {};
    return String(p._bundle_id || p.bundle_id || '').trim();
  }

  function routeUrl(routeKey, fallback) {
    const url = String((window.routes && window.routes[routeKey]) || fallback);
    return /\.js(\?|$)/.test(url) ? url : url.replace(/\/?$/, '') + '.js';
  }

  // XHR POST bypasses monkey-patched fetch from third-party apps.
  function postJson(url, body) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onload = () => {
        let data = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch (_) {}
        if (xhr.status >= 200 && xhr.status < 300 && data !== null) resolve(data);
        else reject(new Error('HTTP ' + xhr.status));
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.send(JSON.stringify(body));
    });
  }

  function fetchCart() {
    return fetch(routeUrl('cart_url', '/cart'), { headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );
  }

  async function refreshBundleCache() {
    if (cache.refreshInFlight) return;
    cache.refreshInFlight = true;
    try {
      const cart = await fetchCart();
      cache.byKey = {};
      cache.byLine = {};
      (cart.items || []).forEach((item, index) => {
        const bundleId = bundleIdFromProperties(item.properties);
        if (!bundleId) return;
        cache.byKey[normalizeItemKey(item.key)] = bundleId;
        const line = typeof item.line === 'number' ? item.line : index + 1;
        cache.byLine[line] = bundleId;
      });
    } catch (_) {
      /* cache stays empty until next refresh */
    } finally {
      cache.refreshInFlight = false;
    }
  }

  function getBundleIdByKey(itemKey) {
    return cache.byKey[normalizeItemKey(itemKey)] || null;
  }

  function getBundleIdByLine(line) {
    const lineNum = parseLine(line);
    return lineNum ? cache.byLine[lineNum] || null : null;
  }

  function getCartRow(line, event) {
    const target = event?.currentTarget;
    if (target?.closest) {
      let row = target.closest(ROW_SELECTOR);
      if (!row && target.closest('cart-remove-button')) {
        row = target.closest('cart-remove-button').closest(ROW_SELECTOR);
      }
      if (row) return row;
    }

    const lineNum = parseLine(line);
    if (!lineNum) return null;
    return document.getElementById('CartDrawer-Item-' + lineNum) || document.getElementById('CartItem-' + lineNum);
  }

  /** DOM row is authoritative; cache is fallback when the row is not rendered. */
  function resolveBundleId(line, event) {
    const row = getCartRow(line, event);
    if (row) {
      const domBundleId = row.getAttribute('data-bundle-id');
      return domBundleId ? domBundleId.trim() : null;
    }
    return getBundleIdByLine(line);
  }

  function stopClick(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function setCartItemsDisabled(disabled) {
    document.getElementById('CartDrawer-CartItems')?.classList.toggle('cart__items--disabled', disabled);
  }

  function getActiveControl(event, quantity) {
    const trigger = event?.currentTarget;
    if (!trigger) return null;

    if (parseInt(quantity, 10) === 0) {
      const removeHost = trigger.closest?.('cart-remove-button');
      if (removeHost) {
        return removeHost.querySelector('.cart-update-line-item__remove-button, a, button') || trigger;
      }
      if (trigger.matches?.('.cart-update-line-item__remove-button, a, button')) return trigger;
      return null;
    }

    if (
      trigger.matches?.('a.custom-quantity-selector__button, button.custom-quantity-selector__button') &&
      !trigger.classList.contains('custom-quantity-selector__button--remove')
    ) {
      return trigger;
    }

    return null;
  }

  function beginBundleLoading(opts = {}) {
    const isCartUpdateDrawer = document.querySelector('cart-drawer.cart_update');
    const setBusy = norseCartUi('setCartDrawerBusy');

    if (!isCartUpdateDrawer) {
      setCartItemsDisabled(true);
      return opts;
    }

    setBusy?.(true);
    opts.control = opts.control || getActiveControl(opts.event, opts.quantity);
    norseCartUi('setRemoveControlLoading')?.(opts.control, true);
    setCartItemsDisabled(true);

    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && opts.line && typeof drawerItems.enableLoading === 'function') {
      drawerItems.enableLoading(opts.line, { skipLineOverlay: true });
    }

    return opts;
  }

  function endBundleLoading(opts = {}) {
    norseCartUi('setRemoveControlLoading')?.(opts.control, false);
    setCartItemsDisabled(false);
    norseCartUi('setCartDrawerBusy')?.(false);

    const drawerItems = document.querySelector('cart-drawer-items');
    if (drawerItems && opts.line && typeof drawerItems.disableLoading === 'function') {
      drawerItems.disableLoading(opts.line, { skipLineOverlay: true });
    }
  }

  function publishCartUpdate(source, cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData });
    }
  }

  async function refreshCartDrawerAfterUpdate(cartData) {
    if (typeof cartData?.item_count === 'number') {
      norseCartUi('updateCartUpdateHeaderCount')?.(cartData.item_count);
    }

    const cartDrawer = document.querySelector('cart-drawer.cart_update') || document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    const isEmpty = cartData?.item_count === 0;
    const hasItems = cartData?.item_count > 0;
    if (isEmpty || hasItems) {
      cartDrawer.classList.toggle('is-empty', isEmpty);
      cartDrawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
    }

    await norseCartUi('refreshCartDrawer')?.();
  }

  function lineItemMatchesBundle(items, line, bundleId) {
    if (line == null || line === '') return true;
    const index = parseLine(line) - 1;
    if (!Number.isFinite(index) || index < 0) return false;
    const item = items[index];
    return Boolean(item && bundleIdFromProperties(item.properties) === bundleId);
  }

  function buildBundleQuantityUpdates(items, bundleId, quantity) {
    return items.map((item) => (bundleIdFromProperties(item.properties) === bundleId ? quantity : item.quantity));
  }

  async function applyCartUpdates(updates, eventSource) {
    const updated = await postJson(routeUrl('cart_update_url', '/cart/update'), { updates });
    if (updated?.item_count === undefined) return null;

    await refreshBundleCache();
    await refreshCartDrawerAfterUpdate(updated);
    publishCartUpdate(eventSource, updated);
    return updated;
  }

  /** @returns {Promise<boolean>} true when a bundle-wide update ran */
  async function bulkUpdateBundle(bundleId, newQty, opts = {}) {
    if (bulkUpdateInFlight) return false;

    bulkUpdateInFlight = true;
    if (opts.quantity === undefined) opts.quantity = newQty;
    beginBundleLoading(opts);

    try {
      const cart = await fetchCart();
      const items = cart.items || [];

      if (!lineItemMatchesBundle(items, opts.line, bundleId)) return false;

      const updates = buildBundleQuantityUpdates(items, bundleId, newQty);
      if (newQty === 0 && !updates.some((qty) => qty === 0)) return false;

      const updated = await applyCartUpdates(updates, 'nbbb-update');
      return updated !== null;
    } catch (err) {
      console.error('[NBB] bulk update error', err);
      return false;
    } finally {
      bulkUpdateInFlight = false;
      endBundleLoading(opts);
    }
  }

  async function cleanupBundleAfterSubscriptionOff(keepKey, bundleId) {
    if (!keepKey) return;
    const resolvedBundleId = bundleId || getBundleIdByKey(keepKey);
    if (!resolvedBundleId) return;

    try {
      const cart = await fetchCart();
      const items = cart.items || [];
      const keepVariantId = String(keepKey).split(':')[0];

      let keepIndex = items.findIndex((item) => normalizeItemKey(item.key) === keepKey);
      if (keepIndex !== -1 && bundleIdFromProperties(items[keepIndex].properties) !== resolvedBundleId) {
        keepIndex = -1;
      }
      if (keepIndex === -1 && keepVariantId) {
        keepIndex = items.findIndex(
          (item) =>
            String(item.variant_id) === keepVariantId &&
            bundleIdFromProperties(item.properties) === resolvedBundleId
        );
      }
      if (keepIndex === -1) return;

      const updates = items.map((item, index) => {
        if (bundleIdFromProperties(item.properties) !== resolvedBundleId) return item.quantity;
        return index === keepIndex ? item.quantity : 0;
      });

      if (!updates.some((qty) => qty === 0)) return;
      await applyCartUpdates(updates, 'nbbb-sub');
    } catch (err) {
      console.error('[NBB] sub cleanup error', err);
    }
  }

  function runSingleLineRemove(host, line, event) {
    const cartItems = host.closest('cart-drawer-items');
    const lineNum = parseLine(line);
    if (!cartItems || lineNum === null || typeof cartItems.updateQuantity !== 'function') return;
    cartItems.updateQuantity(lineNum, 0, { currentTarget: host, ...event });
  }

  function scheduleSubscriptionCleanup(itemKey, bundleId) {
    window._nbbbPendingSubCleanup = { key: itemKey, bundleId };
    setTimeout(async () => {
      const pending = window._nbbbPendingSubCleanup;
      if (!pending) return;
      window._nbbbPendingSubCleanup = false;
      await cleanupBundleAfterSubscriptionOff(pending.key, pending.bundleId);
    }, SUB_CLEANUP_DELAY_MS);
  }

  function handleSubscriptionToggleClick(event) {
    const toggle = event.target.closest?.('.subscription-toggle[role="switch"]');
    if (!toggle || toggle.getAttribute('aria-checked') !== 'true') return;

    const checkbox = toggle.querySelector('.subscription-checkbox[data-item-key]');
    const itemKey = checkbox ? normalizeItemKey(checkbox.dataset.itemKey) : '';
    const bundleId = itemKey ? getBundleIdByKey(itemKey) : null;
    if (itemKey && bundleId) scheduleSubscriptionCleanup(itemKey, bundleId);
  }

  function handleRemoveClick(event) {
    const host =
      event.target.closest?.('cart-remove-button') ||
      event.target.closest?.('.cart-update-line-item__remove-button');
    if (!host) return false;

    const removeControl = host.closest('cart-remove-button') || host;
    const line = removeControl.dataset?.index;
    const bundleId = resolveBundleId(line, { currentTarget: host });
    if (!bundleId) return false;

    stopClick(event);
    bulkUpdateBundle(bundleId, 0, {
      event: { currentTarget: host },
      line,
      quantity: 0,
    }).then((handled) => {
      if (!handled) runSingleLineRemove(host, line, { currentTarget: host });
    });
    return true;
  }

  function handleCartChangeLinkClick(event) {
    const link = event.target.closest?.('[href*="cart/change"]');
    if (!link) return false;

    const href = link.getAttribute('href') || '';
    const idMatch = href.match(/[?&]id=([^&]+)/);
    const qtyMatch = href.match(/[?&]quantity=(\d+)/);
    const lineMatch = href.match(/[?&]line=(\d+)/);

    if (idMatch) {
      const bundleId = getBundleIdByKey(idMatch[1]);
      if (!bundleId) return false;
      stopClick(event);
      bulkUpdateBundle(bundleId, 0, { event: { currentTarget: link }, quantity: 0 });
      return true;
    }

    if (!qtyMatch || !lineMatch) return false;

    const newQty = parseInt(qtyMatch[1], 10);
    const lineNum = parseInt(lineMatch[1], 10);
    if (!Number.isFinite(newQty) || newQty < 0 || !Number.isFinite(lineNum)) return false;

    const bundleId = resolveBundleId(lineNum, { currentTarget: link });
    if (!bundleId) return false;

    stopClick(event);
    bulkUpdateBundle(bundleId, newQty, {
      event: { currentTarget: link },
      line: lineNum,
      quantity: newQty,
    });
    return true;
  }

  function onDocumentClick(event) {
    if (bulkUpdateInFlight) return;
    if (norseCartUi('isCartDrawerBusy')?.()) return;

    if (event.target.closest?.('.subscription-toggle[role="switch"]')) {
      handleSubscriptionToggleClick(event);
      return;
    }

    if (handleRemoveClick(event)) return;
    handleCartChangeLinkClick(event);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (String(event?.source || '').startsWith('nbbb-')) return;
      await refreshBundleCache();

      const pending = window._nbbbPendingSubCleanup;
      if (!pending) return;
      window._nbbbPendingSubCleanup = false;
      if (pending.key && pending.bundleId) {
        await cleanupBundleAfterSubscriptionOff(pending.key, pending.bundleId);
      }
    });
  }

  refreshBundleCache();

  document.addEventListener('click', onDocumentClick, true);

  const originalUpdateQuantity = CartDrawerItems.prototype.updateQuantity;
  CartDrawerItems.prototype.updateQuantity = function (line, quantity, event, name, variantId) {
    const bundleId = resolveBundleId(line, event);
    if (!bundleId) {
      return originalUpdateQuantity.apply(this, arguments);
    }

    return bulkUpdateBundle(bundleId, parseInt(quantity, 10), {
      event,
      line,
      quantity,
    }).then((handled) => {
      if (!handled) {
        return originalUpdateQuantity.call(this, line, quantity, event, name, variantId);
      }
    });
  };
})();


/*
  Re-sync the cart UI after a browser Back / Forward.

  Steps that produce the bug: collection -> PDP -> add to cart -> browser Back. The page
  you land back on is the one from before the add — server-rendered drawer, no badge — so
  the cart looks empty until a reload or a forward navigation. Nothing in the theme handled
  this: a grep for `pageshow` across assets/, layout/ and snippets/ turns up only
  gift-autoadd-extension.js and the igacne A/B test, each re-syncing itself.

  A Back arrives by one of TWO routes and this has to cover both. Handling only the first
  is why an earlier version of this looked like a no-op on the storefront:

  1. bfcache restore — the document is not re-parsed, so DOMContentLoaded never fires a
     second time and none of the theme's init runs again. Seen as `pageshow` + `persisted`.

  2. A real navigation served out of the HTTP cache — `persisted` is false and the theme
     boots normally, but it boots against the stale markup, so the badge is still wrong.
     Seen as a navigation entry of type `back_forward`.

  Route 2 is the one this storefront actually takes in Chrome (measured: the Back reports
  `back_forward`, never a restore — third-party scripts on the page keep it out of bfcache).
  Do not "simplify" this back down to the `persisted` check alone.

  One request for the two cart sections. Failures are swallowed — a stale badge is a
  worse-looking page, not a broken one, and throwing here would log on every Back.
*/
(() => {
  const SECTIONS = 'cart-drawer,cart-icon-bubble';
  let inFlight = null;

  const paintDrawer = (doc) => {
    // Drawer contents and footer, scoped to #CartDrawer so a `.drawer__footer`
    // belonging to some other drawer on the page is never swapped.
    ['cart-drawer-items', '#CartDrawer .drawer__footer'].forEach((selector) => {
      const target = document.querySelector(selector);
      const source = doc.querySelector(selector);
      if (target && source) target.replaceWith(source);
    });

    // The drawer switches between its empty panel and the normal view on this class.
    const live = document.querySelector('cart-drawer');
    const fresh = doc.querySelector('cart-drawer');
    if (live && fresh) live.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
  };

  const paintBubble = (doc) => {
    // `id="cart-icon-bubble"` belongs to the HEADER wrapper, not to anything inside the
    // section — looking for that id in this HTML finds nothing. Take the section's own
    // contents instead.
    //
    // And write to EVERY matching node: the header renders two elements carrying that
    // same id (duplicate ids, invalid but live), so getElementById reaches only one.
    const source = doc.querySelector('.shopify-section') || doc.body;
    if (!source) return;
    document.querySelectorAll('[id="cart-icon-bubble"]').forEach((node) => {
      node.innerHTML = source.innerHTML;
    });
  };

  const resync = () => {
    // `pageshow` can fire more than once for a single restore; never stack two repaints.
    if (inFlight) return inFlight;

    inFlight = (async () => {
      try {
        // Ask the CART route, like the rest of the theme does, and never let this come out
        // of a cache. Both matter, and both were measured on the storefront:
        //
        // - The section response carries NO `cache-control`, only an etag, so Chrome caches
        //   it heuristically. From the second Back onward the browser answered this fetch
        //   with `transferSize: 0` — the copy from the first Back — and the drawer repainted
        //   a cart that was two mutations old. `no-store` is what stops that.
        // - Asking `/` returned an etag of `page_cache:...:IndexController`: Shopify's page
        //   cache for the home page. `routes.cart_url` answers from `CartController`, which
        //   is per-cart. Every other section fetch in this theme already uses cart_url.
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        const url = `${cartUrl}?sections=${SECTIONS}`;
        const response = await fetch(url, { cache: 'no-store' });
        if (!response.ok) return;

        const sections = await response.json();
        const parser = new DOMParser();
        const parse = (key) =>
          typeof sections[key] === 'string' ? parser.parseFromString(sections[key], 'text/html') : null;

        const drawerDoc = parse('cart-drawer');
        if (drawerDoc) paintDrawer(drawerDoc);

        const bubbleDoc = parse('cart-icon-bubble');
        if (bubbleDoc) paintBubble(bubbleDoc);

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } catch (error) {
        console.warn('cart-drawer: could not re-sync the cart after a back/forward navigation', error);
      } finally {
        inFlight = null;
      }
    })();

    return inFlight;
  };

  // Route 1 — bfcache restore.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resync();
  });

  // Route 2 — back/forward navigation that re-parses the document from the HTTP cache.
  // A restore never reaches this branch (no second DOMContentLoaded, and this script does
  // not run again), so the two routes cannot both fire for the same navigation.
  const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (navigation && navigation.type === 'back_forward') {
    // The header may not be parsed yet — wait for a DOM to repaint into.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => resync(), { once: true });
    } else {
      resync();
    }
  }
})();
